// ManiDine Restaurant Web — src/pages/reports/CustomerReports.jsx — Updated: 2026-04-01 16:15 (America/Edmonton)
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import useRestaurantContext from "../../hooks/useRestaurantContext";
import {
  ReportCard,
  ReportChart,
  ReportFilterBar,
  ReportTable,
} from "../../components/reports";

const defaultDateRange = { preset: "7d", start: "", end: "" };

// --- Helpers --------------------------------------------------------

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(Number(value || 0));

const getOrderDate = (createdAt) => {
  if (!createdAt) return null;
  if (typeof createdAt.toDate === "function") return createdAt.toDate();
  if (createdAt instanceof Date) return createdAt;
  const d = new Date(createdAt);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getOrderTimelineDate = (order) =>
  getOrderDate(order?.createdAt || order?.placedAt || order?.orderTime || order?.updatedAt);

const parseNumber = (value, fallback = 0) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

const normalizeStatus = (status) => {
  if (!status) return "";
  return String(status).toLowerCase();
};

const isCustomerOrderEligible = (status) => {
  const s = normalizeStatus(status);
  if (!s) return true;
  return !(
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void") ||
    s.includes("declin")
  );
};

const resolveDateRange = (dateRange) => {
  const now = new Date();
  let start = null;
  let end = null;

  if (dateRange.preset === "today") {
    start = new Date();
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (dateRange.preset === "7d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "30d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "custom") {
    if (dateRange.start) {
      start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
    }
    if (dateRange.end) {
      end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
    }
  }

  return { start, end };
};

const getOrderFoodSubtotal = (order) => {
  if (typeof order?.subtotal === "number") return order.subtotal;
  if (typeof order?.itemsSubtotal === "number") return order.itemsSubtotal;
  if (typeof order?.foodSubtotal === "number") return order.foodSubtotal;

  const total =
    typeof order?.totalAmount === "number"
      ? order.totalAmount
      : typeof order?.total === "number"
      ? order.total
      : null;

  if (typeof total === "number") {
    const tax = parseNumber(order?.taxAmount ?? order?.tax);
    const deliveryFee = parseNumber(order?.deliveryFee);
    const tip =
      typeof order?.tip === "number"
        ? order.tip
        : typeof order?.tipAmount === "number"
        ? order.tipAmount
        : 0;

    return Math.max(total - tax - deliveryFee - tip, 0);
  }

  return 0;
};

const getOrderTax = (order) => parseNumber(order?.taxAmount ?? order?.tax);

const getRestaurantOrderTotal = (order) => {
  const foodSubtotal = getOrderFoodSubtotal(order);
  const tax = getOrderTax(order);
  return foodSubtotal + tax;
};

const getOrderRating = (order) => {
  const r =
    order.rating ??
    order.reviewRating ??
    (order.review && typeof order.review.rating === "number"
      ? order.review.rating
      : undefined);
  return typeof r === "number" ? r : null;
};

// --- Component ------------------------------------------------------

const CustomerReports = () => {
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1) Load all orders for this restaurant
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      if (!restaurantId) {
        setOrders([]);
        setLoading(false);
        return;
      }
      try {
        const ordersRef = collection(db, "orders");
        const ordersQuery = query(
          ordersRef,
          where("restaurantId", "==", restaurantId)
        );
        const snap = await getDocs(ordersQuery);
        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        console.log("[CustomerReports] loaded orders:", rows.length);

        const sample = rows[0];
        if (sample) {
          console.log("[CustomerReports] sample customer fields on order:", {
            rawOrder: sample,
            customerEmail: sample.customerEmail,
            customerName: sample.customerName,
            userId: sample.userId,
          });
        }

        setOrders(rows);
      } catch (err) {
        console.error("[CustomerReports] Load failed:", err);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantLoading) {
      setLoading(true);
      return;
    }

    fetchOrders();
  }, [restaurantId, restaurantLoading]);

  // 2) Date + status filter
  const filteredOrders = useMemo(() => {
    const { start, end } = resolveDateRange(dateRange);
    const result = orders.filter((order) => {
      if (!isCustomerOrderEligible(order.status)) return false;
      const d = getOrderTimelineDate(order);
      if (!d) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
    console.log("[CustomerReports] filtered orders:", result.length);
    return result;
  }, [orders, dateRange]);

  // 3) Aggregate per customer using restaurant-only spend (food + tax)
  const customerStats = useMemo(() => {
    const map = new Map();

    filteredOrders.forEach((order) => {
      const customerId =
        order.customerEmail ||
        order.customerName ||
        order.userId ||
        "guest";

      const name =
        order.customerName ||
        order.customerEmail ||
        order.userId ||
        "Guest";

      const email = order.customerEmail || order.email || "";
      const phone = order.customerPhone || order.phone || "";

      const d = getOrderTimelineDate(order);
      const foodSubtotal = getOrderFoodSubtotal(order);
      const tax = getOrderTax(order);
      const restaurantSpend = getRestaurantOrderTotal(order);
      const rating = getOrderRating(order);

      const prev =
        map.get(customerId) || {
          id: customerId,
          name,
          email,
          phone,
          orders: 0,
          foodSales: 0,
          taxCollected: 0,
          restaurantSpend: 0,
          firstOrderAt: d,
          lastOrderAt: d,
          ratingSum: 0,
          ratingCount: 0,
        };

      map.set(customerId, {
        ...prev,
        name: name || prev.name,
        email: email || prev.email,
        phone: phone || prev.phone,
        orders: prev.orders + 1,
        foodSales: prev.foodSales + foodSubtotal,
        taxCollected: prev.taxCollected + tax,
        restaurantSpend: prev.restaurantSpend + restaurantSpend,
        firstOrderAt:
          !prev.firstOrderAt || (d && d < prev.firstOrderAt)
            ? d
            : prev.firstOrderAt,
        lastOrderAt:
          !prev.lastOrderAt || (d && d > prev.lastOrderAt)
            ? d
            : prev.lastOrderAt,
        ratingSum: rating != null ? prev.ratingSum + rating : prev.ratingSum,
        ratingCount:
          rating != null ? prev.ratingCount + 1 : prev.ratingCount,
      });
    });

    return [...map.values()].map((customer) => ({
      ...customer,
      avgRating:
        customer.ratingCount > 0
          ? customer.ratingSum / customer.ratingCount
          : null,
    }));
  }, [filteredOrders]);

  // 4) KPIs
  const kpis = useMemo(() => {
    const totalCustomers = customerStats.length;
    const totalOrders = filteredOrders.length;

    const newCustomers = customerStats.filter((customer) => customer.orders === 1).length;
    const repeatCustomers = totalCustomers - newCustomers;
    const repeatRate =
      totalCustomers > 0 ? (repeatCustomers / totalCustomers) * 100 : 0;

    const avgOrdersPerCustomer =
      totalCustomers > 0 ? totalOrders / totalCustomers : 0;

    const restaurantSales = customerStats.reduce(
      (sum, customer) => sum + customer.restaurantSpend,
      0
    );
    const avgRestaurantSpendPerCustomer =
      totalCustomers > 0 ? restaurantSales / totalCustomers : 0;

    let ratingSum = 0;
    let ratingCount = 0;
    customerStats.forEach((customer) => {
      if (customer.avgRating != null) {
        ratingSum += customer.avgRating;
        ratingCount += 1;
      }
    });
    const avgRating = ratingCount > 0 ? ratingSum / ratingCount : null;

    return {
      totalCustomers,
      newCustomers,
      repeatCustomers,
      repeatRate,
      avgOrdersPerCustomer,
      restaurantSales,
      avgRestaurantSpendPerCustomer,
      avgRating,
    };
  }, [customerStats, filteredOrders.length]);

  // 5) Chart data: new vs repeat
  const newVsRepeatChart = useMemo(
    () => [
      { type: "New", value: kpis.newCustomers },
      { type: "Repeat", value: kpis.repeatCustomers },
    ],
    [kpis.newCustomers, kpis.repeatCustomers]
  );

  // 6) Tables
  const topCustomers = useMemo(
    () =>
      customerStats
        .slice()
        .sort((a, b) => b.restaurantSpend - a.restaurantSpend)
        .slice(0, 20),
    [customerStats]
  );

  const recentCustomers = useMemo(
    () =>
      customerStats
        .slice()
        .sort((a, b) => {
          const da = a.lastOrderAt ? a.lastOrderAt.getTime() : 0;
          const db = b.lastOrderAt ? b.lastOrderAt.getTime() : 0;
          return db - da;
        })
        .slice(0, 20),
    [customerStats]
  );

  const formatDateTime = (d) => {
    if (!d) return "—";
    return d.toLocaleString();
  };

  if (restaurantLoading) {
    return (
      <div className="reports-section">
        <p className="muted">Loading restaurant…</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="reports-section">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </div>
    );
  }

  // --- Render -------------------------------------------------------

  return (
    <div className="reports-section">
      <ReportFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        grouping={null}
        onGroupingChange={null}
      />

      <section
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 14,
          background: "rgba(15, 23, 42, 0.04)",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        Customer value on this page is based on <strong>restaurant sales only</strong>: food subtotal
        plus tax. Delivery fees and customer tips are excluded so this page stays aligned with the
        corrected restaurant report totals.
      </section>

      {/* KPIs */}
      <section className="reports-kpi-grid">
        <ReportCard title="Total customers" value={kpis.totalCustomers} />
        <ReportCard title="New customers" value={kpis.newCustomers} />
        <ReportCard title="Repeat customers" value={kpis.repeatCustomers} />
        <ReportCard
          title="Repeat rate"
          value={
            Number.isFinite(kpis.repeatRate)
              ? `${kpis.repeatRate.toFixed(1)}%`
              : "0%"
          }
        />
        <ReportCard
          title="Avg orders / customer"
          value={kpis.avgOrdersPerCustomer.toFixed(2)}
        />
        <ReportCard
          title="Restaurant sales"
          value={formatCurrency(kpis.restaurantSales)}
        />
        <ReportCard
          title="Avg restaurant spend / customer"
          value={formatCurrency(kpis.avgRestaurantSpendPerCustomer)}
        />
        <ReportCard
          title="Average rating"
          value={
            kpis.avgRating != null
              ? kpis.avgRating.toFixed(2)
              : "No ratings"
          }
        />
      </section>

      {/* Chart */}
      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">
            New vs repeat customers
          </h3>
          <ReportChart
            type="bar"
            data={newVsRepeatChart}
            xKey="type"
            series={[{ dataKey: "value", name: "Customers" }]}
          />
        </div>
      </section>

      {/* Tables */}
      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Top customers</h3>
          <ReportTable
            columns={[
              { title: "Name", dataIndex: "name", key: "name" },
              { title: "Email", dataIndex: "email", key: "email" },
              { title: "Phone", dataIndex: "phone", key: "phone" },
              {
                title: "Orders",
                dataIndex: "orders",
                key: "orders",
              },
              {
                title: "Food sales",
                dataIndex: "foodSales",
                key: "foodSales",
                render: (value) => formatCurrency(value),
              },
              {
                title: "Tax",
                dataIndex: "taxCollected",
                key: "taxCollected",
                render: (value) => formatCurrency(value),
              },
              {
                title: "Restaurant spend",
                dataIndex: "restaurantSpend",
                key: "restaurantSpend",
                render: (value) => formatCurrency(value),
              },
              {
                title: "Last order",
                dataIndex: "lastOrderAt",
                key: "lastOrderAt",
                render: (_, row) => formatDateTime(row.lastOrderAt),
              },
            ]}
            data={topCustomers}
          />
        </div>

        <div className="reports-column">
          <h3 className="reports-section-title">Recent customers</h3>
          <ReportTable
            columns={[
              { title: "Name", dataIndex: "name", key: "name" },
              { title: "Email", dataIndex: "email", key: "email" },
              { title: "Phone", dataIndex: "phone", key: "phone" },
              {
                title: "Orders in range",
                dataIndex: "orders",
                key: "orders",
              },
              {
                title: "Restaurant spend",
                dataIndex: "restaurantSpend",
                key: "restaurantSpend",
                render: (value) => formatCurrency(value),
              },
              {
                title: "Last order",
                dataIndex: "lastOrderAt",
                key: "lastOrderAt",
                render: (_, row) => formatDateTime(row.lastOrderAt),
              },
              {
                title: "Avg rating",
                dataIndex: "avgRating",
                key: "avgRating",
                render: (value) =>
                  value != null ? value.toFixed(2) : "—",
              },
            ]}
            data={recentCustomers}
          />
        </div>
      </section>

      {loading && (
        <div className="reports-loading">Loading data…</div>
      )}
    </div>
  );
};

export default CustomerReports;
